import axios from 'src/utils/axios';

// Host Templates v2 — template categories list actions.
//
// Reference data fetched once on login and cached in Redux. Categories
// describe the grouping sections of the Host Templates list (name,
// systemCode, sortOrder, day-offset window). See the templateCategories
// table. Phase: the six locked SYSTEM categories (salespersonId=1).

// ── Action Types ──

export const GET_TEMPLATE_CATEGORIES = '@template-categories/get-list';
export const GET_TEMPLATE_CATEGORIES_SUCCESS = '@template-categories/get-list-success';
export const GET_TEMPLATE_CATEGORIES_ERROR = '@template-categories/get-list-error';

// ── Action Creator ──

/** *************************************************************************
 * getTemplateCategories
 *
 * Fetches the categories list. Fired non-blocking on login from Auth.js.
 * On error, dispatches GET_TEMPLATE_CATEGORIES_ERROR — never throws.
 * Login UX must not be degraded by a slow reference endpoint.
 *************************************************************************** */
export const getTemplateCategories = (scopeType, scopeId) => async (dispatch) => {
  dispatch({ type: GET_TEMPLATE_CATEGORIES });
  try {
    // With a scope, the server returns the system six PLUS the consultant's
    // own custom categories at that scope (for the Edit Categories modal).
    // With no scope, the request is byte-identical to the login fetch
    // (system six only) that Phase 3a's grouping relies on.
    const config = (scopeType && scopeId) ? { params: { scopeType, scopeId } } : undefined;
    const response = await axios.get('/api/templateCategories', config);
    const templateCategories = Array.isArray(response?.data?.templateCategories)
      ? response.data.templateCategories
      : [];
    dispatch({
      type: GET_TEMPLATE_CATEGORIES_SUCCESS,
      payload: { templateCategories },
    });
  } catch (error) {
    dispatch({
      type: GET_TEMPLATE_CATEGORIES_ERROR,
      payload: { error: error?.message || 'Failed to load template categories' },
    });
  }
};

// ── Write thunks (Host Templates v2 Phase 3b) ──
//
// Create/update/delete consultant-owned custom categories. Unlike the
// read thunk above (fail-soft on login), these surface errors to the
// caller so the Edit Categories modal can show them. On success they
// refresh the categories cache.
//
// `cascadeToOpenParties` carries the "also update OPEN events with this
// Event Type?" choice. It is only meaningful on a partyType-scope save;
// the server ignores it for party-scope rows.

/** *************************************************************************
 * createTemplateCategory
 *************************************************************************** */
export const createTemplateCategory = (payload) => async (dispatch) => {
  const response = await axios.post('/api/templateCategories', payload);
  // Refresh scoped so the new custom category appears in the modal's list.
  await dispatch(getTemplateCategories(payload?.scopeType, payload?.scopeId));
  return response?.data;
};

/** *************************************************************************
 * updateTemplateCategory
 *************************************************************************** */
export const updateTemplateCategory = (id, payload, scope = {}) => async (dispatch) => {
  const response = await axios.put(`/api/templateCategories/${id}`, payload);
  await dispatch(getTemplateCategories(scope?.scopeType, scope?.scopeId));
  return response?.data;
};

/** *************************************************************************
 * deleteTemplateCategory
 *************************************************************************** */
export const deleteTemplateCategory = (id, scope = {}) => async (dispatch) => {
  const response = await axios.delete(`/api/templateCategories/${id}`);
  await dispatch(getTemplateCategories(scope?.scopeType, scope?.scopeId));
  return response?.data;
};
